/**
 * @file useDialogDeferredContent
 * @description 弹窗关闭动画结束后再清空展示数据，避免关闭过程中内容闪空。
 *
 * @whenToUse 基于 Dialog 的业务弹窗，关闭时父级会同步清空 project / form / list 等驱动内容。
 * @whenNotToUse 无关闭动画的即时卸载；内容不依赖 open 外部 props 的静态弹窗。
 */

import { useEffect, useState } from 'react'
import { DIALOG_CLOSE_ANIMATION_MS } from '@/components/ui/dialog.tsx'

/**
 * 在弹窗关闭后延迟销毁展示内容，与 Dialog 关闭动画对齐。
 *
 * @param open - 弹窗是否打开
 * @param content - 当前应展示的数据；关闭动画结束后置为 `null`
 * @returns 关闭动画期间保留的上次内容，完全关闭后为 `null`
 *
 * @example
 * ```tsx
 * const displayProject = useDialogDeferredContent(open, project)
 * const displayFiles = useDialogDeferredContent(open, files)
 * ```
 */
export function useDialogDeferredContent<T>(open: boolean, content: T): T | null {
  const [displayContent, setDisplayContent] = useState<T | null>(open ? content : null)

  useEffect(() => {
    if (open) {
      setDisplayContent(content)
      return
    }
    const timer = window.setTimeout(() => {
      setDisplayContent(null)
    }, DIALOG_CLOSE_ANIMATION_MS)
    return () => window.clearTimeout(timer)
  }, [open, content])

  return displayContent
}
